import {
  createColumnHelper,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import PaginatedTable from '../Common/PaginatedTable';
import { displayDateTime } from '../../Utils';
import { UserClockIn } from '../../Models/UserClockIn';
import { User } from '../../Models/User';
import { HiPencilAlt } from 'react-icons/hi';
import { useState } from 'react';
import TimesheetEditModal from './TimesheetEditModal';

export default function TimesheetAdminTable({
  data,
  users,
  refreshData,
}: {
  data: UserClockIn[];
  users: User[];
  refreshData: () => void;
}) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedClockIn, setSelectedClockIn] = useState<UserClockIn | null>(null);

  const columnHelper = createColumnHelper<UserClockIn>();

  const getUserName = (userId: number | null) => {
    if (!userId || !users) return '';
    const user = users.find(u => u.id === userId);
    return user ? `${user.firstName} ${user.lastName}` : '';
  };

  const columns = [
    columnHelper.accessor((row) => row.id, {
      id: 'id',
      header: '',
      cell: (info) => (
        <div className='flex justify-center gap-1'>
          <button
            type='button'
            className='buttonMain p-2'
            onClick={() => {
              setSelectedClockIn(info.row.original);
              setShowEditModal(true);
            }}
          >
            <HiPencilAlt />
          </button>
        </div>
      ),
      footer: (info) => info.column.id,
      size: 35,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => getUserName(row.userId), {
      id: 'userName',
      header: 'User',
      cell: (info) => (
        <div className='cursor-pointer'>
          {getUserName(info.row.original.userId)}
        </div>
      ),
      footer: (info) => info.column.id,
      size: 150,
    }),
    columnHelper.accessor((row) => row.timeIn, {
      id: 'timeIn',
      header: 'Clock In',
      cell: (info) => (
        <div>
          {info.getValue() ? displayDateTime(info.getValue()!) : ''}
        </div>
      ),
      footer: (info) => info.column.id,
      size: 150,
    }),
    columnHelper.accessor((row) => row.timeOut, {
      id: 'timeOut',
      header: 'Clock Out',
      cell: (info) => (
        <div>
          {info.getValue() ? displayDateTime(info.getValue()!) : ''}
        </div>
      ),
      footer: (info) => info.column.id,
      size: 150,
    }),
    columnHelper.accessor((row) => {
      if (row.timeIn && row.timeOut) {
        const timeIn = new Date(row.timeIn);
        const timeOut = new Date(row.timeOut);
        const diffMs = timeOut.getTime() - timeIn.getTime();
        const diffHrs = diffMs / (1000 * 60 * 60);
        return diffHrs.toFixed(2);
      }
      return '';
    }, {
      id: 'hours',
      header: 'Hours',
      cell: (info) => (
        <div>
          {info.getValue()}
        </div>
      ),
      footer: (info) => info.column.id,
      size: 100,
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  return (
    <>
      <PaginatedTable
        table={table}
        dataCount={data.length}
        columnResizeMode="onChange"
      />
      <TimesheetEditModal
        show={showEditModal}
        setShow={setShowEditModal}
        selectedClockIn={selectedClockIn}
        setSelectedClockIn={setSelectedClockIn}
        refreshData={refreshData}
        users={users}
      />
    </>
  );
}